import React from 'react';
import { Linking } from 'react-native';
import {
  ContentContainer,
  Copy,
  List,
  BulletPoint,
  BulletSymbol,
  BulletText,
  BulletColumn,
  NestedList,
  Link,
} from '@/shared/styles/styledComponents/legalTextComponents';
import BackButton from '@/shared/components/BackButton';
import useBreakpoint from '@/shared/hooks/useBreakpoint';
import { useRouter } from 'expo-router';
import { ROUTES } from '@/shared/constants/routes';

const PrivacyPolicy: React.FC = () => {
  const { isDesktop } = useBreakpoint();
  const router = useRouter();

  const openUrl = (url: string) => {
    Linking.openURL(url).catch(() => {
      // handle error if needed
    });
  };

  return (
    <ContentContainer isDesktop={isDesktop}>
      <BackButton containerStyle={{ marginBottom: 16 }} />

      {/* Title and Intro */}
      <Copy bold style={{ marginBottom: 16 }}>
        Privacy Policy
      </Copy>
      <Copy style={{ marginBottom: 12 }}>Last Updated: March 1, 2026</Copy>
      <Copy style={{ marginBottom: 12 }}>
        EMME Styling Corp. (&quot;EMME Styling&quot; or &quot;we&quot; or
        &quot;us&quot; or &quot;our&quot;) respects your privacy. This Privacy
        Policy (&quot;Policy&quot;) describes the types of personal information
        (&quot;Personal Information&quot;) we collect from you when you access
        our website (
        <Link onPress={() => openUrl('https://www.emmestyling.com')}>
          www.emmestyling.com
        </Link>
        ) and use our services (&quot;Services&quot;), and provides details
        regarding how this Personal Information will be collected, used,
        transmitted, disclosed or stored by us.
      </Copy>
      <Copy style={{ marginBottom: 12 }}>
        Please read this Policy carefully to fully understand how we collect,
        use, share, and protect your Personal Information and, if you do not
        agree with this Policy, please do not use our Services.
      </Copy>
      <Copy style={{ marginBottom: 24 }}>
        This Policy is also governed by and incorporates by reference those
        certain EMME Styling{' '}
        <Link onPress={() => router.push(ROUTES.TERMS.path)}>
          Terms of Service
        </Link>{' '}
        accessible through our website in connection with any use of (and which
        shall be agreed to prior to using) our Services.
      </Copy>

      {/* Section: Information We Collect and Use */}
      <Copy bold style={{ marginBottom: 16 }}>
        Information We Collect and Use
      </Copy>

      <List>
        {/* • Information You Give Us — nested ◦ */}
        <BulletPoint style={{ marginBottom: 16 }}>
          <BulletSymbol>•</BulletSymbol>
          <BulletColumn>
            <Copy style={{ marginBottom: 8 }}>
              <Copy bold>Information You Give Us.</Copy> When you sign up for
              and use our Services as a customer or styling expert
              (&quot;Stylist&quot;), we may collect and store Personal
              Information you provide, including:
            </Copy>
            <NestedList>
              <BulletPoint>
                <BulletSymbol>◦</BulletSymbol>
                <BulletText>
                  <Copy bold>Personal Identifiers.</Copy> Personal identifiers
                  like your name, email address, mailing address, telephone
                  number, or signature may be collected and used in order to
                  communicate with you and process your requests; for customer
                  service purposes, for informational purposes, and to
                  communicate with you for marketing purposes, which may include
                  updates regarding special offers and/or new services that we
                  believe may be of interest to you.
                </BulletText>
              </BulletPoint>
              <BulletPoint>
                <BulletSymbol>◦</BulletSymbol>
                <BulletText>
                  <Copy bold>Communications and Submissions.</Copy> When you
                  interact with us via our website, our customer agent
                  representatives and other employees, or our Stylists, we
                  collect information you provide. Information collected here
                  will be used to process requests and manage our relationship
                  with, and services provided to, you.
                </BulletText>
              </BulletPoint>
              <BulletPoint>
                <BulletSymbol>◦</BulletSymbol>
                <BulletText>
                  <Copy bold>Referral Information.</Copy> If you use our
                  referral program and recommend our Services to a friend or
                  family member, you may need to provide contact information for
                  yourself and the friend or family member, respectively. We
                  will automatically send your friend or family member a
                  one-time email inviting him or her to visit the website. We
                  will store this information for the sole purpose of sending
                  this one-time email and tracking the success of our referral
                  program.
                </BulletText>
              </BulletPoint>
              <BulletPoint>
                <BulletSymbol>◦</BulletSymbol>
                <BulletText>
                  <Copy bold>Financial Information.</Copy> We receive financial
                  information in the form of credit card or other related
                  banking information to process payments for the Services you
                  purchase and to issue Stylist and referral payments, as
                  applicable.
                </BulletText>
              </BulletPoint>
              <BulletPoint>
                <BulletSymbol>◦</BulletSymbol>
                <BulletText>
                  <Copy bold>Commercial Information.</Copy> We collect and
                  generate commercial, and transaction, information to respond
                  to and fulfill requests, improve our Services offerings,
                  communicate with you, tailor our marketing efforts, and for
                  internal reporting purposes.
                </BulletText>
              </BulletPoint>
              <BulletPoint>
                <BulletSymbol>◦</BulletSymbol>
                <BulletText>
                  <Copy bold>
                    Inferences from the information listed above.
                  </Copy>{' '}
                  Ultimately, we will use the information we collect to help us
                  understand your preferences to help us develop and provide
                  better Services.
                </BulletText>
              </BulletPoint>
              <BulletPoint>
                <BulletSymbol>◦</BulletSymbol>
                <BulletText>
                  <Copy bold>Audio and Visual Information.</Copy> We may store
                  digital photos you share with us through our Services. We may
                  record customer service telephone calls for quality control
                  purposes and to meet our legal obligations. We may record
                  video calls between you and the Stylists.
                </BulletText>
              </BulletPoint>
              <BulletPoint>
                <BulletSymbol>◦</BulletSymbol>
                <BulletText>
                  <Copy bold>Privacy Choices.</Copy> We receive and retain any
                  opt-outs or other choices related to how we collect, use, and
                  disclose Personal Information.
                </BulletText>
              </BulletPoint>
            </NestedList>
          </BulletColumn>
        </BulletPoint>

        {/* • Information From Third Parties — nested ◦ */}
        <BulletPoint style={{ marginBottom: 16 }}>
          <BulletSymbol>•</BulletSymbol>
          <BulletColumn>
            <Copy style={{ marginBottom: 8 }}>
              <Copy bold>Information From Third Parties.</Copy> We may, from
              time to time, supplement the information we collect about you
              online with outside records from third parties in order to enhance
              our ability to serve you, to tailor our content to you and to
              offer you opportunities to engage Services that we believe may be
              of interest to you. These third-party sources include:
            </Copy>
            <NestedList>
              <BulletPoint>
                <BulletSymbol>◦</BulletSymbol>
                <BulletText>
                  <Copy bold>Social Media Features.</Copy> Our website includes
                  interactive social media features that will be hosted by a
                  third party or directly on our website. Such features may
                  collect your IP address, identify websites you are visiting,
                  and may set cookies to enable them to function as you have
                  requested.
                </BulletText>
              </BulletPoint>
              <BulletPoint>
                <BulletSymbol>◦</BulletSymbol>
                <BulletText>
                  <Copy bold>Marketing Partners and Data Providers.</Copy> We
                  may collect information about you from marketing partners and
                  data providers. This information may include items such as
                  personal identifiers, commercial information, and other
                  information to help us determine what Services may be of
                  interest to you.
                </BulletText>
              </BulletPoint>
              <BulletPoint>
                <BulletSymbol>◦</BulletSymbol>
                <BulletText>
                  <Copy bold>Affiliates.</Copy> We may collect personal
                  identifiers, commercial information, and other generally
                  available information from EMME Styling&apos;s affiliate
                  websites and other providers of advertisements.
                </BulletText>
              </BulletPoint>
              <BulletPoint>
                <BulletSymbol>◦</BulletSymbol>
                <BulletText>
                  <Copy bold>Information Provided by Other Users.</Copy> Other
                  users may also provide us with information about you, which
                  may include personal identifiers or other information in order
                  to invite you to use our Services or to share information
                  about our Services to you. Your information will be used
                  solely for the reasons it was provided.
                </BulletText>
              </BulletPoint>
            </NestedList>
          </BulletColumn>
        </BulletPoint>

        {/* • Other Information We Collect */}
        <BulletPoint style={{ marginBottom: 16 }}>
          <BulletSymbol>•</BulletSymbol>
          <BulletColumn>
            <Copy style={{ marginBottom: 24 }}>
              <Copy bold>Other Information We Collect.</Copy> We also may
              collect other information about you, your devices, or your use of
              the Services. In this case, we will provide more details when this
              may be necessary or otherwise obtain your consent. The choice to
              share information with us is yours. In the event you do not wish
              to share certain information, it may affect your ability to use
              some of our Services.
            </Copy>
          </BulletColumn>
        </BulletPoint>

        {/* • Usage Information — nested ◦ */}
        <BulletPoint style={{ marginBottom: 16 }}>
          <BulletSymbol>•</BulletSymbol>
          <BulletColumn>
            <Copy style={{ marginBottom: 8 }}>
              <Copy bold>
                Information We Get from Your Use of Our Services and Stylists.
              </Copy>{' '}
              We collect information about the Services and Stylists that you
              use and how you use them (&quot;Usage Information&quot;). Usage
              Information is obtained when you interact with or otherwise engage
              Stylists (whether virtually or in person), open our emails,
              interact with our content, and click on our advertisements.
              Similar to the above, we use this information to improve our
              Services, tailor our marketing efforts, and to aggregate
              information regarding website usage and activity. Usage
              Information includes, but is not limited to:
            </Copy>
            <NestedList>
              <BulletPoint>
                <BulletSymbol>◦</BulletSymbol>
                <BulletText>
                  <Copy bold>Devices.</Copy> Information about the physical
                  device you use, its operating system, your phone number, and
                  mobile network information.
                </BulletText>
              </BulletPoint>
              <BulletPoint>
                <BulletSymbol>◦</BulletSymbol>
                <BulletText>
                  <Copy bold>Browsing Information.</Copy> We may collect your
                  URL, information regarding the website pages you visit, and
                  the time and duration of your website visit.
                </BulletText>
              </BulletPoint>
              <BulletPoint>
                <BulletSymbol>◦</BulletSymbol>
                <BulletText>
                  <Copy bold>IP Address.</Copy> Your IP address applicable to
                  whatever device you use to access our website.
                </BulletText>
              </BulletPoint>
              <BulletPoint>
                <BulletSymbol>◦</BulletSymbol>
                <BulletText>
                  <Copy bold>Location Information.</Copy> We may collect and use
                  your geolocation information when you use our website, which
                  can be determined by looking at your IP address, GPS, access
                  points, nearby devices, and cell towers.
                </BulletText>
              </BulletPoint>
            </NestedList>
          </BulletColumn>
        </BulletPoint>

        {/* • Automatic Collection — nested ◦ */}
        <BulletPoint style={{ marginBottom: 16 }}>
          <BulletSymbol>•</BulletSymbol>
          <BulletColumn>
            <Copy style={{ marginBottom: 8 }}>
              <Copy bold>Automatic Collection from Cookies and More.</Copy>{' '}
              Certain information may be automatically collected when you use
              our Services in order to help us improve our websites and
              communications. The following technologies enable us to collect
              such Usage Information:
            </Copy>
            <NestedList>
              <BulletPoint>
                <BulletSymbol>◦</BulletSymbol>
                <BulletText>
                  <Copy bold>Cookies.</Copy> A cookie is a small file that is
                  sent to your computer when you visit a website; it allows the
                  site to recognize your browser upon subsequent visits. Cookies
                  store user preferences and certain other information. You may
                  elect to refuse all cookies or make specific selections. This,
                  however, may impact the functionality of our Services.
                </BulletText>
              </BulletPoint>
              <BulletPoint>
                <BulletSymbol>◦</BulletSymbol>
                <BulletText>
                  <Copy bold>Mobile Identifiers.</Copy> Software development
                  kits (&quot;SDKs&quot;) that use mobile advertising
                  identifiers (i.e., IDFA on Apple mobile devices or Advertising
                  ID on Android mobile devices) function similarly to cookies,
                  but on your mobile device. Preferences may be modified in the
                  Settings function of your device.
                </BulletText>
              </BulletPoint>
              <BulletPoint>
                <BulletSymbol>◦</BulletSymbol>
                <BulletText>
                  <Copy bold>Web Beacons.</Copy> Web Beacons are small graphic
                  images or other web programming code that may be included on
                  our website and in any communications sent to you
                  electronically. While this may not be visible to you, these
                  technologies are used to monitor the number of visitors to our
                  website, to determine how our Services are navigated and used,
                  to determine how our emails are interacted with, and to make
                  cookies function properly.
                </BulletText>
              </BulletPoint>
            </NestedList>
          </BulletColumn>
        </BulletPoint>

        {/* • Anonymous or Aggregated Information */}
        <BulletPoint style={{ marginBottom: 16 }}>
          <BulletSymbol>•</BulletSymbol>
          <BulletColumn>
            <Copy>
              <Copy bold>Anonymous or Aggregated Information.</Copy> We
              de-identify or aggregate data we receive and may use and disclose
              it for any business purpose.
            </Copy>
          </BulletColumn>
        </BulletPoint>

        {/* • EMME Styling / Customer–Stylist interactions */}
        <BulletPoint style={{ marginBottom: 24 }}>
          <BulletSymbol>•</BulletSymbol>
          <BulletColumn>
            <Copy>
              EMME Styling is not involved in the conversations, consultations
              or, in general, engagements by and between Customers and Stylists.
              EMME Styling is not and shall not be responsible for - nor liable
              in connection with - any services provided by Stylists or
              interactions by and between Customers and Stylists (whether
              virtually or in person). EMME Styling disclaims any and all
              liability (whether foreseeable or not) with regard to the
              foregoing, and all Users of the Site hereby acknowledge this and
              fully release EMME Styling from any and all responsibility or
              liability with regard to any and all interactions by and between
              any Customer(s) and any Stylist(s). You understand and acknowledge
              that EMME Styling cannot and does not edit, modify, filter,
              screen, monitor, endorse or guarantee the content of interactions.
              EMME Styling shall not be liable for any acts or omissions of
              Stylists, content in communications or the ability of Stylists to
              answer questions. We cannot ensure that a Customer and/or a
              Stylist will complete a transaction. Notwithstanding the
              foregoing, EMME Styling reserves the right, but is not obligated,
              to refuse to post or to remove any content and/or remove any
              User&apos;s access to the Site.
            </Copy>
          </BulletColumn>
        </BulletPoint>
      </List>

      {/* Section: How We Disclose Personal Information */}
      <Copy bold style={{ marginBottom: 16 }}>
        How We Disclose Personal Information
      </Copy>

      <List>
        <BulletPoint style={{ marginBottom: 16 }}>
          <BulletSymbol>1.</BulletSymbol>
          <BulletColumn>
            <Copy bold style={{ marginBottom: 4 }}>
              Service Providers.
            </Copy>
            <Copy>
              We share your Personal Information with service providers that
              provide Services to you and assist us with marketing, payment
              processing, website maintenance, email administration, and
              analyzing activity on our website. We have informed our service
              providers that the use of Personal Information shall be strictly
              limited to the purposes specified in our agreements.
            </Copy>
          </BulletColumn>
        </BulletPoint>

        <BulletPoint style={{ marginBottom: 16 }}>
          <BulletSymbol>2.</BulletSymbol>
          <BulletColumn>
            <Copy bold style={{ marginBottom: 4 }}>
              Our Partners.
            </Copy>
            <Copy>
              If you interact with any of our partners to request details about
              their products or services, we may share your personal identifiers
              and commercial information to enable them to communicate with you.
            </Copy>
          </BulletColumn>
        </BulletPoint>

        <BulletPoint style={{ marginBottom: 16 }}>
          <BulletSymbol>3.</BulletSymbol>
          <BulletColumn>
            <Copy bold style={{ marginBottom: 4 }}>
              Within Our Organization.
            </Copy>
            <Copy>
              We may share any of your Personal Information with parents,
              subsidiaries, joint venture partners, and other entities under
              common control subject to this Privacy Policy.
            </Copy>
          </BulletColumn>
        </BulletPoint>

        <BulletPoint style={{ marginBottom: 16 }}>
          <BulletSymbol>4.</BulletSymbol>
          <BulletColumn>
            <Copy bold style={{ marginBottom: 4 }}>
              Social Networks.
            </Copy>
            <Copy>
              If you use any of our website features and Services that can
              prompt information to be published to your social networks, we ask
              that you please refer to the privacy policies of the relevant
              social networks to understand how they will use your Personal
              Information.
            </Copy>
          </BulletColumn>
        </BulletPoint>

        <BulletPoint style={{ marginBottom: 16 }}>
          <BulletSymbol>5.</BulletSymbol>
          <BulletColumn>
            <Copy bold style={{ marginBottom: 4 }}>
              Public Posts.
            </Copy>
            <Copy>
              As explained in the Terms of Service, any information you provide
              when you interact with our website and our Stylists, by way of
              posts including reviews and testimonials (&quot;Posts&quot;) may
              not be private or confidential. Posts may be accessed by others
              without yours or our knowledge and will not be covered by the
              protections of this Policy. We have no control over public posts
              and advise that you exercise caution and use your judgment when
              disclosing any Personal Information to any outside services.
            </Copy>
          </BulletColumn>
        </BulletPoint>

        <BulletPoint style={{ marginBottom: 16 }}>
          <BulletSymbol>6.</BulletSymbol>
          <BulletColumn>
            <Copy bold style={{ marginBottom: 4 }}>
              Sweepstakes, Contests, and Promotions.
            </Copy>
            <Copy>
              We may offer promotions (&quot;Promotions&quot;) that may require
              registration. If you register for any of our Promotions, you are
              agreeing to the terms, conditions, rules, and use of your Personal
              Information for such Promotion.
            </Copy>
          </BulletColumn>
        </BulletPoint>

        <BulletPoint style={{ marginBottom: 16 }}>
          <BulletSymbol>7.</BulletSymbol>
          <BulletColumn>
            <Copy bold style={{ marginBottom: 4 }}>
              Business Transfers.
            </Copy>
            <Copy>
              In the event of a merger, acquisition, reorganization, bankruptcy,
              receivership, sale of company assets, your Personal Information,
              and any other information that we have collected over time, may be
              provided/disclosed to the purchaser, court, or other persons
              and/or entities as necessary to complete the transaction as
              permitted by law or contract.
            </Copy>
          </BulletColumn>
        </BulletPoint>

        <BulletPoint style={{ marginBottom: 16 }}>
          <BulletSymbol>8.</BulletSymbol>
          <BulletColumn>
            <Copy bold style={{ marginBottom: 4 }}>
              Legal and Similar Disclosures.
            </Copy>
            <Copy>
              We may disclose Personal Information about you with individuals
              connected to various legal matters including law enforcement,
              attorneys, advisors, and courts, if doing so is required by law,
              is necessary to enforce our contracts, or if doing so will ensure
              protection of our users, Stylists, website, and company.
            </Copy>
          </BulletColumn>
        </BulletPoint>

        <BulletPoint style={{ marginBottom: 24 }}>
          <BulletSymbol>9.</BulletSymbol>
          <BulletColumn>
            <Copy bold style={{ marginBottom: 4 }}>
              Otherwise with Your Consent or At Your Direction.
            </Copy>
            <Copy>
              We also may share your Personal Information when you direct us to
              do so, or otherwise consent to our requests.
            </Copy>
          </BulletColumn>
        </BulletPoint>
      </List>

      {/* Section: Opt-Out Choices */}
      <Copy bold style={{ marginBottom: 16 }}>
        Opt-Out Choices
      </Copy>
      <Copy style={{ marginBottom: 12 }}>
        We will always happily answer your questions regarding the use of
        Personal Information. Below is a list of options you have to exercise
        your preferences:
      </Copy>

      <List>
        <BulletPoint style={{ marginBottom: 16 }}>
          <BulletSymbol>•</BulletSymbol>
          <BulletColumn>
            <Copy bold style={{ marginBottom: 4 }}>
              Email Communications.
            </Copy>
            <Copy>
              When you receive marketing communications from us, you may
              indicate a preference to stop receiving further marketing
              communications from us. You can &quot;opt-out&quot; by following
              the unsubscribe instructions provided in the email you receive or
              by contacting us directly at{' '}
              <Link onPress={() => openUrl('mailto:support@emmestyling.com')}>
                support@emmestyling.com
              </Link>
              . We may, however, continue to send you email communications that
              are otherwise necessary for the administration of our website,
              your account, and the Services, and to inform you of any changes
              to this Policy or our Terms of Service.
            </Copy>
          </BulletColumn>
        </BulletPoint>

        <BulletPoint style={{ marginBottom: 16 }}>
          <BulletSymbol>•</BulletSymbol>
          <BulletColumn>
            <Copy bold style={{ marginBottom: 4 }}>
              Update or Delete Your Account information.
            </Copy>
            <Copy>
              If you wish to update or delete any of the Personal Information
              provided to us in connection with your account, please email us at{' '}
              <Link onPress={() => openUrl('mailto:support@emmestyling.com')}>
                support@emmestyling.com
              </Link>
              , or update the Account Information on your dashboard. In the
              event that we are required by law, or for any other reason to keep
              certain information, we will maintain it for the time and reason
              necessary only. Please note that some residual data may be stored
              in backups and may not be immediately deleted. If you have any
              particular questions regarding this, please email us at{' '}
              <Link onPress={() => openUrl('mailto:support@emmestyling.com')}>
                support@emmestyling.com
              </Link>
              .
            </Copy>
          </BulletColumn>
        </BulletPoint>

        <BulletPoint style={{ marginBottom: 16 }}>
          <BulletSymbol>•</BulletSymbol>
          <BulletColumn>
            <Copy bold style={{ marginBottom: 4 }}>
              Opt-out of Personalized Advertising.
            </Copy>
            <Copy>
              We will never sell your personal information to third parties. If
              you would prefer to opt-out of receiving any type of advertising
              or communications from our partners, please email us at{' '}
              <Link onPress={() => openUrl('mailto:support@emmestyling.com')}>
                support@emmestyling.com
              </Link>
              .
            </Copy>
          </BulletColumn>
        </BulletPoint>
      </List>

      <Copy style={{ marginBottom: 12 }}>
        In addition to emailing us with your questions or requests, you can:
      </Copy>
      <List>
        <BulletPoint>
          <BulletSymbol>•</BulletSymbol>
          <BulletText>
            Access the privacy settings in your browser and mobile device.
          </BulletText>
        </BulletPoint>
        <BulletPoint>
          <BulletSymbol>•</BulletSymbol>
          <BulletText>
            Click on the Ad Choices icon in certain advertisements.
          </BulletText>
        </BulletPoint>
        <BulletPoint>
          <BulletSymbol>•</BulletSymbol>
          <BulletText>
            Visit the Network Advertising Initiative&apos;s Website (
            <Link
              onPress={() =>
                openUrl(
                  'http://www.networkadvertising.org/managing/opt_out.asp',
                )
              }
            >
              http://www.networkadvertising.org/managing/opt_out.asp
            </Link>
            ) for further advertising opt-out options.
          </BulletText>
        </BulletPoint>
        <BulletPoint>
          <BulletSymbol>•</BulletSymbol>
          <BulletText>
            Visit the Digital Advertising Alliance (DAA) Opt-Out Page (
            <Link onPress={() => openUrl('http://www.aboutads.info/choices')}>
              http://www.aboutads.info/choices
            </Link>
            ) where you will be able to identify companies that have used
            Cookies on your browser.
          </BulletText>
        </BulletPoint>
      </List>

      <Copy style={{ marginBottom: 24 }}>
        Please note that choosing to remove your information (i.e. opt-out),
        does not mean that all advertisements will be removed from websites; it
        instead means that advertisements will no longer be specifically
        tailored to your preferences, and information regarding your usage may
        still be collected for research, analytics or internal operations
        purposes.
      </Copy>

      {/* Section: California Privacy Rights */}
      <Copy bold style={{ marginBottom: 16 }}>
        California Privacy Rights.
      </Copy>
      <Copy style={{ marginBottom: 8 }}>
        Per the California Consumer Privacy Act of 2018 (&quot;CCPA&quot;),
        California residents can:
      </Copy>
      <List>
        <BulletPoint>
          <BulletSymbol>•</BulletSymbol>
          <BulletText>
            Request the deletion of the Personal Information we have about them
          </BulletText>
        </BulletPoint>
        <BulletPoint>
          <BulletSymbol>•</BulletSymbol>
          <BulletText>
            Ask for additional details regarding our collection, use and
            disclosure of their Personal Information
          </BulletText>
        </BulletPoint>
      </List>
      <Copy style={{ marginBottom: 16 }}>
        California residents are also protected from any discriminatory
        treatment for exercising such rights.
      </Copy>

      <Copy bold style={{ marginBottom: 4 }}>
        Requests for Deletion and to Know About Information Collected.
      </Copy>
      <Copy style={{ marginBottom: 16 }}>
        California residents can email{' '}
        <Link onPress={() => openUrl('mailto:support@emmestyling.com')}>
          support@emmestyling.com
        </Link>{' '}
        to make such requests. Requests for deletion or for information will
        require us to request additional information for the purposes of
        verifying the information we have on file. This means that California
        residents will be required to submit their full name and email address
        and, potentially, their address and phone number, for verification
        purposes only.
      </Copy>
      <Copy style={{ marginBottom: 24 }}>
        California law also allows California residents to appoint an authorized
        agent to make privacy rights requests. Any authorized agents must
        provide proof of the California resident&apos;s written permission that
        indicates that such person is authorized to submit the request on the
        California resident&apos;s behalf. Such agents will also be required to
        comply with verification procedures.
      </Copy>

      {/* Section: Other California Requirements */}
      <Copy bold style={{ marginBottom: 16 }}>
        Other California Requirements.
      </Copy>
      <Copy style={{ marginBottom: 24 }}>
        California residents under the age of 18 and any registered user of the
        Services may request and obtain removal of content or information you
        have publicly posted to our website or the Services by contacting us at{' '}
        <Link onPress={() => openUrl('mailto:support@emmestyling.com')}>
          support@emmestyling.com
        </Link>
        .
      </Copy>

      {/* Section: Nevada Privacy Rights */}
      <Copy bold style={{ marginBottom: 16 }}>
        Nevada Privacy Rights.
      </Copy>
      <Copy style={{ marginBottom: 24 }}>
        We do not sell consumers&apos; covered information. If you are a Nevada
        resident, you have the right to specifically request that we not to sell
        your Personal Information. To submit such a request, please email us at{' '}
        <Link onPress={() => openUrl('mailto:support@emmestyling.com')}>
          support@emmestyling.com
        </Link>
        .
      </Copy>

      {/* Section: International Residents */}
      <Copy bold style={{ marginBottom: 16 }}>
        International Residents.
      </Copy>
      <Copy style={{ marginBottom: 24 }}>
        EMME Styling Corp. is a Florida registered, company that provides its
        Services from the United States. If you reside anywhere outside of the
        United States, please be aware that information we collect will be
        transferred to and processed in the United States and may be processed
        and stored in other countries outside the United States which may have
        data protection laws that differ from the laws in your country. By using
        our Services, or providing us with any information, you consent to the
        collection, processing, maintenance and transfer of such information in
        and to the United States and to other countries that may have different
        protections from us.
      </Copy>

      {/* Section: Security */}
      <Copy bold style={{ marginBottom: 16 }}>
        Security
      </Copy>
      <Copy style={{ marginBottom: 16 }}>
        We have safeguards in place to help protect against loss, misuse,
        unauthorized access, disclosure, alteration, or destruction of the
        Personal Information you provide. Unfortunately, transmission of data
        can never be 100% secure. It may be possible for third parties not under
        our control to intercept or access transmissions or private
        communications unlawfully. Therefore, any transmission of Personal
        Information to us is made at your own risk.
      </Copy>
      <Copy style={{ marginBottom: 24 }}>
        We retain your account information and the information provided by you
        on Posts to comply with certain legal obligations, and may do so
        indefinitely until no longer required, or until no longer relevant to
        our business Services, at which time we may delete and/or aggregate and
        store it indefinitely.
      </Copy>

      {/* Section: Third Party Links */}
      <Copy bold style={{ marginBottom: 16 }}>
        Third Party Links
      </Copy>
      <Copy style={{ marginBottom: 24 }}>
        Any links to third party websites/services that may be on our
        website/Services, will be accessed by leaving our website. Accessing
        other websites subjects you to the terms of use and privacy policies of
        those websites, which may be different from ours. We do not operate or
        control any website or Services except our own. Please carefully review
        the terms of use and privacy policy of any website you access to ensure
        you fully understand their specific privacy, collection and use
        practices.
      </Copy>

      {/* Section: Children's Personal Information */}
      <Copy bold style={{ marginBottom: 16 }}>
        Children&apos;s Personal Information
      </Copy>
      <Copy style={{ marginBottom: 24 }}>
        We do not knowingly collect or store any Personal Information from
        children under the age of 18. If a parent or guardian becomes aware that
        his or her child under the age of 18 has provided us with information
        without his or her consent, please email us at{' '}
        <Link onPress={() => openUrl('mailto:support@emmestyling.com')}>
          support@emmestyling.com
        </Link>
        . We will delete such information as required by law.
      </Copy>

      {/* Section: Updates to this Privacy Policy */}
      <Copy bold style={{ marginBottom: 16 }}>
        Updates to this Privacy Policy
      </Copy>
      <Copy style={{ marginBottom: 16 }}>
        This Policy may be revised from time to time to reflect changes in the
        law or technology, or changes in our collection and use practices. We
        will promptly notify you of any material changes to how we collect and
        use your Personal Information. Such notice may be made on our website.
        You agree that we may notify you about material changes regarding this
        Policy by placing a notice on our website/Services.
      </Copy>
      <Copy style={{ marginBottom: 24 }}>
        Any changes to this Policy will become effective as of the &quot;Last
        Updated&quot; date at the top of the page. Your continued use of our
        Services will be deemed to be your agreement to, and acceptance of such
        changes. If you do not agree with the terms of this Policy, as it may be
        amended from time to time, in whole or in part, please discontinue use
        of the Services.
      </Copy>

      {/* Section: How to Contact Us */}
      <Copy bold style={{ marginBottom: 16 }}>
        How to Contact Us
      </Copy>
      <Copy style={{ marginBottom: 24 }}>
        If you have any questions or suggestions regarding this Privacy Policy,
        contact us at{' '}
        <Link onPress={() => openUrl('mailto:support@emmestyling.com')}>
          support@emmestyling.com
        </Link>
        .
      </Copy>
    </ContentContainer>
  );
};

export default PrivacyPolicy;
